/**
 * Turning what a model read off a card into a row this application can hold.
 *
 * The `extract-business-card` Edge Function returns the card as printed —
 * "(205) 555-0147", "REALTOR®", "Birmingham, AL 35242-1234". None of that is
 * storable: the phone column takes E.164 or nothing, the category takes one of
 * ten values, the county is derived. So the endpoint normalises nothing and
 * this does all of it, in one place the review screen and any future importer
 * both use (CLAUDE.md rule 5).
 *
 * The governing rule is **a bad reading must degrade to a blank, never to a
 * wrong value.** A blank field is one the rep types in while the card is still
 * in their hand. A wrong mobile number is a text to a stranger, found out
 * weeks later by somebody who is annoyed. So every field that cannot be
 * trusted is dropped and marked, rather than coerced.
 */

import { findLineOfWork, normalizeLineOfWork } from "./lineOfWork.js";
import {
  PARTNER_CATEGORIES,
  countyForZip,
  toE164,
  type PartnerCategory,
  type PartnerCounty,
} from "./partners.js";

/** Whatever the endpoint handed back. Every field may be missing or wrong. */
export type RawCardExtraction = Record<string, unknown>;

export type FieldConfidence = "high" | "low";

/** The fields a card can fill in, in the order the review screen shows them. */
export const CARD_FIELDS = [
  "first_name",
  "last_name",
  "company",
  "title",
  "line_of_work",
  "category",
  "phone_mobile",
  "phone_office",
  "email",
  "website",
  "address_line1",
  "city",
  "state",
  "zip",
  "linkedin",
  "instagram",
  "facebook",
] as const;

export type CardField = (typeof CARD_FIELDS)[number];

export interface NormalizedCard {
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  title: string | null;
  /** "Plumber", "Electrician": what the card file groups by (ADR 0262). */
  line_of_work: string | null;
  category: PartnerCategory;
  phone_mobile: string | null;
  phone_office: string | null;
  email: string | null;
  website: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county: PartnerCounty;
  linkedin: string | null;
  instagram: string | null;
  facebook: string | null;
  /** Per field: whether it is worth a second look before saving. */
  confidence: Partial<Record<CardField, FieldConfidence>>;
  /** Legible text the model could not place. Shown, never stored as a field. */
  unrecognized_text: string | null;
}

const text = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * A job title, without the symbols a card prints and a text message should not.
 *
 * "REALTOR®" is how the National Association of Realtors requires its members
 * to print it, and it is correct on a card. In a CRM field it is a character
 * that renders as a box on half the screens it reaches.
 */
const cleanTitle = (value: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;
  return text(raw.replace(/[®™©]/g, ""));
};

/**
 * An email address, or nothing.
 *
 * Checked rather than trusted: the commonest OCR failure on a card is reading
 * an email out of a line that is actually a website, and the second commonest
 * is losing the @ to a low-contrast background. Both produce a string that is
 * not an address, and both are better as a blank the rep fills in.
 */
const email = (value: unknown): string | null => {
  const raw = text(value)
    ?.toLowerCase()
    .replace(/^mailto:/, "");
  if (!raw) return null;
  return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/.test(raw) ? raw : null;
};

/** A website, given a protocol so a link on the partner page works. */
const website = (value: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  // A bare domain is what is printed on a card. Anything with a space in it is
  // a tagline the model mistook for a URL.
  if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(raw)) return `https://${raw}`;
  return null;
};

/** Two letters, upper case, or nothing. "Alabama" becomes AL. */
const state = (value: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return raw.toLowerCase() === "alabama" ? "AL" : null;
};

/** Five digits. A ZIP+4 keeps its first five; anything else is dropped. */
const zip = (value: unknown): string | null => {
  const raw = text(value);
  if (!raw) return null;
  const match = /^(\d{5})(?:-\d{4})?$/.exec(raw);
  return match ? match[1]! : null;
};

/**
 * A social handle, as a handle rather than as a URL.
 *
 * Cards print both, and a mixture of the two in a column reads badly. The
 * handle is what somebody would type into the app, so that is what is kept —
 * except for Facebook, where a page name is often several words and the URL
 * is the only usable form.
 */
const handle = (value: unknown, host: RegExp): string | null => {
  const raw = text(value);
  if (!raw) return null;
  const fromUrl = host.exec(raw);
  if (fromUrl) return `@${fromUrl[1]!.replace(/\/$/, "")}`;
  if (raw.startsWith("@")) return raw;
  if (/^[\w.]+$/.test(raw)) return `@${raw}`;
  return raw;
};

const category = (value: unknown): PartnerCategory => {
  const raw = text(value)?.toLowerCase().replace(/\s+/g, "_");
  return (PARTNER_CATEGORIES as readonly string[]).includes(raw ?? "")
    ? (raw as PartnerCategory)
    : "other";
};

function confidenceMap(
  value: unknown,
): Partial<Record<CardField, FieldConfidence>> {
  if (typeof value !== "object" || value === null) return {};
  const source = value as Record<string, unknown>;
  const map: Partial<Record<CardField, FieldConfidence>> = {};
  for (const field of CARD_FIELDS) {
    const level = source[field];
    if (level === "low" || level === "high") map[field] = level;
  }
  return map;
}

/**
 * The card, ready for the review form.
 *
 * Anything that failed to normalise comes back null **and marked `low`**, so a
 * phone number the model read as "205-555" is not merely dropped — the review
 * screen highlights the empty box and the rep looks at the photograph beside
 * it. Silently dropping it would be the same as never reading the card, minus
 * the part where somebody notices.
 */
export function normalizeCardExtraction(
  raw: RawCardExtraction | null | undefined,
): NormalizedCard {
  const source = raw ?? {};
  const confidence = confidenceMap(source.confidence);

  /** Mark a field low when the model gave something and normalising lost it. */
  const dropped = (field: CardField, given: unknown, kept: unknown) => {
    if (kept === null && text(given) !== null) confidence[field] = "low";
  };

  const phone_mobile = toE164(text(source.phone_mobile));
  const phone_office = toE164(text(source.phone_office));
  const normalizedEmail = email(source.email);
  const normalizedWebsite = website(source.website);
  const normalizedZip = zip(source.zip);
  const normalizedState = state(source.state);

  dropped("phone_mobile", source.phone_mobile, phone_mobile);
  dropped("phone_office", source.phone_office, phone_office);
  dropped("email", source.email, normalizedEmail);
  dropped("website", source.website, normalizedWebsite);
  dropped("zip", source.zip, normalizedZip);
  dropped("state", source.state, normalizedState);

  // A card the model could not categorise arrives as "other", which is a real
  // answer — but if it guessed something this application does not have, the
  // rep should pick, not inherit a silent fallback.
  const lineOfWork = normalizeLineOfWork(text(source.line_of_work));
  // A line of work the list knows settles the category too, so a card read as
  // "Electrician" is never left under "Other" for the texting side.
  const listed = findLineOfWork(lineOfWork);
  const normalizedCategory = listed?.category ?? category(source.category);
  if (
    !listed &&
    normalizedCategory === "other" &&
    text(source.category) !== null &&
    text(source.category)?.toLowerCase() !== "other"
  ) {
    confidence.category = "low";
  }

  return {
    first_name: text(source.first_name),
    last_name: text(source.last_name),
    company: text(source.company),
    title: cleanTitle(source.title),
    line_of_work: lineOfWork,
    category: normalizedCategory,
    phone_mobile,
    phone_office,
    email: normalizedEmail,
    website: normalizedWebsite,
    address_line1: text(source.address_line1),
    city: text(source.city),
    state: normalizedState,
    zip: normalizedZip,
    county: countyForZip(normalizedZip),
    linkedin: handle(
      source.linkedin,
      /linkedin\.com\/(?:in|company)\/([^/?#]+)/i,
    ),
    instagram: handle(source.instagram, /instagram\.com\/([^/?#]+)/i),
    facebook: text(source.facebook),
    confidence,
    unrecognized_text: text(source.unrecognized_text),
  };
}

/**
 * The fields worth a second look, in the order they appear on the form.
 *
 * Used to draw the highlight and to count "3 fields to check" above it. A rep
 * who has just photographed a card will read a short list; they will not read
 * a form where nine boxes are yellow.
 */
export function fieldsToCheck(card: NormalizedCard): CardField[] {
  return CARD_FIELDS.filter((field) => card.confidence[field] === "low");
}

/**
 * Is there enough here to save?
 *
 * A name or a company. Deliberately low: the row's own constraint says the
 * same thing, and a card that yielded only a company name is still a card
 * worth keeping — the rep will fill the rest in from memory on the drive back.
 */
export function cardHasSomebody(card: NormalizedCard): boolean {
  return Boolean(card.first_name || card.last_name || card.company);
}

/* ------------------------------------------------------------- the vCard -- */

const vcardEscape = (value: string): string =>
  value.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,");

/**
 * A contact card the rep can save to their iPhone.
 *
 * This matters more than it sounds. The texts go out from the rep's personal
 * phone, and a text from a number Messages cannot name arrives in a thread
 * headed by ten digits. Saving the partner to Contacts first means the reply,
 * when it comes, arrives under their name — which is the difference between a
 * reply the rep answers and one they scroll past.
 *
 * CRLF line endings and the `END:VCARD` terminator are both required by
 * RFC 6350; iOS silently refuses a vCard with Unix line endings.
 */
export function partnerVcard(card: {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  title?: string | null;
  phone_mobile?: string | null;
  phone_office?: string | null;
  email?: string | null;
  website?: string | null;
}): string {
  const first = card.first_name?.trim() ?? "";
  const last = card.last_name?.trim() ?? "";
  const display =
    [first, last].filter(Boolean).join(" ") ||
    card.company?.trim() ||
    "Partner";

  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${vcardEscape(last)};${vcardEscape(first)};;;`,
    `FN:${vcardEscape(display)}`,
  ];

  if (card.company) lines.push(`ORG:${vcardEscape(card.company)}`);
  if (card.title) lines.push(`TITLE:${vcardEscape(card.title)}`);
  if (card.phone_mobile) lines.push(`TEL;TYPE=CELL:${card.phone_mobile}`);
  if (card.phone_office) lines.push(`TEL;TYPE=WORK:${card.phone_office}`);
  if (card.email) lines.push(`EMAIL;TYPE=INTERNET:${card.email}`);
  if (card.website) lines.push(`URL:${card.website}`);

  lines.push("END:VCARD");
  return `${lines.join("\r\n")}\r\n`;
}
